import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';

/**
 * Die Startseite: ein Wegweiser, keine Liste.
 *
 * Der Unterschied ist der Kern der Seite. Eine Rangfolge von sechs
 * Vorschlägen beantwortet die Frage nicht — "Platz 1 von 6" sagt nichts
 * darüber, ob Platz 1 den Abend wert ist. Deshalb steht oben EINE Aussage
 * mit einer Stufe, und alles darunter ist Begründung, nicht Auswahl.
 */
export default function HeutePage() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [mehr, setMehr] = useState(false);

  useEffect(() => {
    api.plan(7).then(setPlan).catch((e) => setFehler(e.message));
  }, [user?.id]);

  if (fehler) return <div className="error">{fehler}</div>;
  if (!plan) return <div className="skeleton" style={{ height: 320 }} />;

  const [heute, ...weitere] = plan.tage;

  // Was unter "nur nebenbei" liegt, versteckt sich hinter einem Klick. Sonst
  // ist die Seite wieder eine Liste, in der alles gleich aussieht.
  const wichtig = heute.aktionen.filter((a) => (a.urteil?.rang ?? 2) >= 2);
  const rest    = heute.aktionen.filter((a) => (a.urteil?.rang ?? 2) < 2);
  const sichtbar = mehr ? heute.aktionen : wichtig;

  return (
    <>
      {user && !plan.zieleGesetzt && (
        <div className="hinweisleiste">
          <div>
            <strong>Sag, worauf du hinspielst.</strong> Ohne deine Ziele rate ich nur.
          </div>
          <Link className="btn btn--klein" to="/mein-bereich/ziele">Ziele setzen</Link>
        </div>
      )}

      <Wegweiser tag={heute} />

      {sichtbar.length > 0 && (
        <section className="section">
          <h2 className="section__title">Was heute sonst geht</h2>
          <div className="stack stack--eng">
            {sichtbar.slice(heute.wegweiser.aktionId ? 1 : 0).map((a) => (
              <AktionsZeile key={a.id} aktion={a} />
            ))}
          </div>
          {!mehr && rest.length > 0 && (
            <button className="btn btn--ghost btn--klein" onClick={() => setMehr(true)}>
              {rest.length} unwichtigere anzeigen
            </button>
          )}
        </section>
      )}

      <section className="section">
        <h2 className="section__title">Die nächsten Tage</h2>
        <div className="stack stack--eng">
          {weitere.map((t) => (
            <Link key={t.datum} to="/events" className={'tagzeile tagzeile--' + t.wegweiser.stufe}>
              <span className="tagzeile__tag">
                <strong>{t.wochentag.slice(0, 2)}</strong>
                <span>{datum(t.datum)}</span>
              </span>
              <span className="tagzeile__text">{t.wegweiser.titel}</span>
              <span className={'stufe stufe--' + t.wegweiser.stufe}>{stufenWort(t.wegweiser.stufe)}</span>
            </Link>
          ))}
        </div>
      </section>

      {plan.dauerhaft.length > 0 && (
        <section className="section">
          <h2 className="section__title">Läuft ohnehin</h2>
          <p className="gruppe__text">Nichts davon musst du einplanen.</p>
          <div className="stack stack--eng">
            {plan.dauerhaft.map((a) => <AktionsZeile key={a.id} aktion={a} leise />)}
          </div>
        </section>
      )}
    </>
  );
}

/** Die eine Aussage. Gross, farbig, ohne Nebensätze. */
function Wegweiser({ tag }) {
  const w = tag.wegweiser;

  return (
    <section className={'wegweiser wegweiser--' + w.stufe}>
      <span className="wegweiser__marke">
        Heute · {tag.wochentag}, {datum(tag.datum)}
      </span>
      <h1 className="wegweiser__titel">{w.titel}</h1>
      {w.satz && <p className="wegweiser__satz">{w.satz}</p>}
    </section>
  );
}

function AktionsZeile({ aktion, leise = false }) {
  // Notnagel, kein Schönheitsfehler: Eine Aktion ohne Urteil hat die Seite
  // schon einmal komplett abgeschossen. Die Zeile darf blass aussehen, sie
  // darf nicht verhindern, dass der Rest erscheint.
  const urteil = aktion.urteil ?? { stufe: 'offen', text: 'Offen', rang: 2 };

  return (
    <article className={'azeile' + (leise ? ' azeile--leise' : '')}>
      <span className={'stufe stufe--' + urteil.stufe}>{urteil.text}</span>

      <div className="azeile__mitte">
        <div className="azeile__titel">
          {aktion.eventSlug
            ? <Link to={'/event/' + aktion.eventSlug}>{aktion.titel}</Link>
            : aktion.titel}
        </div>
        <div className="azeile__warum">{aktion.warum}</div>
      </div>

      <span className="azeile__zeit">{zeitfenster(aktion)}</span>
    </article>
  );
}

function stufenWort(stufe) {
  return {
    pflicht: 'Hingehen', lohnt: 'Lohnt', nebenbei: 'Nebenbei',
    egal: 'Egal', ruhig: 'Ruhig', nichts: 'Nichts', vorbei: 'Vorbei',
  }[stufe] ?? 'Offen';
}

function zeitfenster(a) {
  if (a.von && a.bis) {
    if (a.zustand === 'vorbei') return 'war ' + uhr(a.von) + '–' + uhr(a.bis);
    if (a.zustand === 'laeuft') return 'noch bis ' + uhr(a.bis);
    return uhr(a.von) + '–' + uhr(a.bis);
  }
  if (a.kosten?.raidPaesse) return a.kosten.raidPaesse + ' Pass';
  if (a.endetAm) return 'bis ' + datum(a.endetAm.slice(0, 10));
  return '';
}

function uhr(wert) {
  return new Date(wert.replace(' ', 'T'))
    .toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function datum(wert) {
  return new Date(wert + (wert.length === 10 ? 'T00:00:00' : ''))
    .toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
}
